import React, { useState } from "react";
import { Select, DatePicker, notification, Input } from "antd";
import moment from "moment";
import { INTERNAL_STATUSES } from "../../constants/status";
import { SERVICE_PROVIDER_DETAILS } from "../../constants/serviceProviders";

const CodOrderFilters = ({ filters, onFiltersChange }) => {
  const [searchField, setSearchField] = useState("trackingNo");
  const today = moment();
  const oneWeekAgo = moment().subtract(7, "days");

  const searchBy = (
    <Select
      defaultValue="trackingNo"
      className="select-before"
      onChange={(value) => setSearchField(value)}
      style={{ width: 130 }}
    >
      <Select.Option value="trackingNo">Tracking No</Select.Option>
      <Select.Option value="name">Receiver</Select.Option>
      <Select.Option value="orderId">Order Id</Select.Option>
    </Select>
  );

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: "16px 24px",
        alignItems: "center",
      }}
    >
      {/* Sort By */}
      <div>
        <label className="block mb-1 text-sm font-medium">Sort By</label>
        <Select
          placeholder="Sort By"
          style={{ width: "100%" }}
          allowClear
          value={filters.sortBy}
          onChange={(value) =>
            onFiltersChange({
              ...filters,
              sortBy: value,
              // Clear ALL date fields first, then set the one for selected type
              startDate: undefined,
              endDate: undefined,
              paymentStartDate: undefined,
              paymentEndDate: undefined,
              remittedStartDate: undefined,
              remittedEndDate: undefined,
              // Then set defaults for selected
              ...(value === "paymentDate" && {
                paymentStartDate: oneWeekAgo,
                paymentEndDate: today,
              }),
              ...(value === "createdDate" && {
                startDate: oneWeekAgo,
                endDate: today,
              }),
              ...(value === "remittedDate" && {
                remittedStartDate: oneWeekAgo,
                remittedEndDate: today,
              }),
            })
          }
        >
          <Select.Option value="paymentDate">Order Payment Date</Select.Option>
          <Select.Option value="createdDate">Order Created Date</Select.Option>
          <Select.Option value="remittedDate">Order Remitted Date</Select.Option>
        </Select>
      </div>

      {/* Dynamic Date Range */}
      {filters.sortBy === "createdDate" && (
        <div>
          <label className="block mb-1 text-sm font-medium">Order Created At</label>
          <DatePicker.RangePicker
            allowClear={false}
            allowEmpty={false}
            style={{ width: "100%" }}
            value={[filters.startDate, filters.endDate]}
            onChange={(dates) => {
              if (dates && dates[1].diff(dates[0], "days") > 90) {
                notification.error({
                  message: "Date Range cannot be more than 90 days.",
                  placement: "bottomRight",
                });
                return;
              }
              onFiltersChange({
                ...filters,
                startDate: dates?.[0],
                endDate: dates?.[1],
                paymentStartDate: undefined,
                paymentEndDate: undefined,
                remittedStartDate: undefined,
                remittedEndDate: undefined,
              });
            }}
          />
        </div>
      )}

      {filters.sortBy === "paymentDate" && (
        <div>
          <label className="block mb-1 text-sm font-medium">Order Checkout At</label>
          <DatePicker.RangePicker
            allowClear={false}
            allowEmpty={false}
            style={{ width: "100%" }}
            value={[filters.paymentStartDate, filters.paymentEndDate]}
            onChange={(dates) => {
              if (dates && dates[1].diff(dates[0], "days") > 90) {
                notification.error({
                  message: "Date Range cannot be more than 90 days.",
                  placement: "bottomRight",
                });
                return;
              }
              onFiltersChange({
                ...filters,
                paymentStartDate: dates?.[0],
                paymentEndDate: dates?.[1],
                startDate: undefined,
                endDate: undefined,
                remittedStartDate: undefined,
                remittedEndDate: undefined,
              });
            }}
          />
        </div>
      )}

      {filters.sortBy === "remittedDate" && (
        <div>
          <label className="block mb-1 text-sm font-medium">Order Remitted At</label>
          <DatePicker.RangePicker
            allowClear={false}
            allowEmpty={false}
            style={{ width: "100%" }}
            value={[filters.remittedStartDate, filters.remittedEndDate]}
            onChange={(dates) => {
              if (dates && dates[1].diff(dates[0], "days") > 90) {
                notification.error({
                  message: "Date Range cannot be more than 90 days.",
                  placement: "bottomRight",
                });
                return;
              }
              onFiltersChange({
                ...filters,
                remittedStartDate: dates?.[0],
                remittedEndDate: dates?.[1],
                startDate: undefined,
                endDate: undefined,
                paymentStartDate: undefined,
                paymentEndDate: undefined,
              });
            }}
          />
        </div>
      )}

      {/* COD Currency */}
      <div>
        <label className="block mb-1 text-sm font-medium">COD Currency</label>
        <Select
          style={{ width: "100%" }}
          defaultValue={filters.codCurrencyCode}
          onChange={(value) => onFiltersChange({ ...filters, codCurrencyCode: value })}
        >
          <Select.Option value="RM">RM</Select.Option>
          <Select.Option value="SGD">SGD</Select.Option>
        </Select>
      </div>

      {/* Next Day Remittance */}
      <div>
        <label className="block mb-1 text-sm font-medium">Next Day Remittance</label>
        <Select
          placeholder="ALL"
          style={{ width: "100%" }}
          allowClear
          value={filters.isNextDayRemittance || undefined}
          onChange={(value) => onFiltersChange({ ...filters, isNextDayRemittance: value })}
        >
          <Select.Option value={true}>Yes</Select.Option>
          <Select.Option value={false}>No</Select.Option>
        </Select>
      </div>

      {/* Is Remitted */}
      <div>
        <label className="block mb-1 text-sm font-medium">Is Remitted</label>
        <Select
          placeholder="ALL"
          style={{ width: "100%" }}
          allowClear
          value={filters.isRemittance || undefined}
          onChange={(value) => onFiltersChange({ ...filters, isRemittance: value })}
        >
          <Select.Option value={true}>Yes</Select.Option>
          <Select.Option value={false}>No</Select.Option>
        </Select>
      </div>

      {/* Internal Status */}
      <div>
        <label className="block mb-1 text-sm font-medium">Internal Status</label>
        <Select
          mode="multiple"
          placeholder="ALL"
          style={{ width: "100%" }}
          allowClear
          value={filters.internalStatus || undefined}
          onChange={(value) => onFiltersChange({ ...filters, internalStatus: value })}
        >
          {INTERNAL_STATUSES.map((status) => (
            <Select.Option key={status} value={status}>
              {status}
            </Select.Option>
          ))}
        </Select>
      </div>

      {/* Courier */}
      <div>
        <label className="block mb-1 text-sm font-medium">Courier</label>
        <Select
          placeholder="ALL"
          style={{ width: "100%" }}
          allowClear
          value={filters.serviceProvider}
          onChange={(value) => onFiltersChange({ ...filters, serviceProvider: value })}
        >
          {SERVICE_PROVIDER_DETAILS.map(({ key, name }) => (
            <Select.Option key={key} value={key}>
              {name}
            </Select.Option>
          ))}
        </Select>
      </div>

      {/* Search */}
      <div style={{ gridColumn: "1 / -1" }}>
        <label className="block mb-1 text-sm font-medium">Search</label>
        <Input.Search
          addonBefore={searchBy}
          placeholder="Search"
          onSearch={(term) =>
            onFiltersChange({ ...filters, search: term, searchField })
          }
          style={{ width: "100%" }}
        />
      </div>

      {/* Inline responsive CSS */}
      <style>
        {`
          @media (max-width: 768px) {
            div[style*="display: grid"] {
              grid-template-columns: 1fr !important;
              gap: 12px !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default CodOrderFilters;
